import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';

import {
  createCheckoutSession,
  createTopupSession,
} from 'src/api/functions/subscriptionApi';
import { isErrorResponseResult } from 'src/api/utils/errorHandlers';
import { queryNames } from 'src/api/utils/queryNames';
import { config } from 'src/utils/config';
import { useAccessToken } from './useAccessToken';

/**
 * Stripe Embedded Checkout (in-app modal).
 *
 * `startCheckout(planCode)` / `startTopup(sku)` ask the backend for an embedded
 * Checkout Session (`POST /api/v1/billing/checkout-session` / `/topup-session`)
 * and expose its `clientSecret`, which the caller mounts in a `<CheckoutModal>`
 * (Stripe's EmbeddedCheckout, rendered in an iframe inside our own modal — the
 * user never leaves the page). The session is created with
 * `redirect_on_completion='never'`, so completion is signalled by
 * `handleComplete` (the EmbeddedCheckout `onComplete` callback) — we poll-refresh
 * the subscription and close the modal. The `checkout.session.completed` webhook
 * applies the change server-side.
 *
 * `enabled` is false when Stripe isn't configured on the frontend
 * (`REACT_APP_STRIPE_PUBLISHABLE_KEY` unset) — callers fall back to
 * contact-support copy.
 */
export function useStripeCheckout() {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const enabled = Boolean(config.stripePublishableKey);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const inFlight = useRef(false);

  // Refresh the subscription after checkout so the plan / balance / banners
  // update without a manual reload. Our webhook applies the change server-side
  // ~1-2s after the order, so poll-invalidate a few times to catch it.
  const pollRefresh = useCallback(() => {
    let ticks = 0;
    const refresh = () => {
      queryClient.invalidateQueries([queryNames.subscription]);
      ticks += 1;
      if (ticks < 6) window.setTimeout(refresh, 2500);
    };
    refresh();
  }, [queryClient]);

  const closeCheckout = useCallback(() => setClientSecret(null), []);

  // EmbeddedCheckout onComplete: the session completed successfully. Refresh so
  // the plan/balance land, then close the modal shortly after (leaving Stripe's
  // built-in success state visible for a moment).
  const handleComplete = useCallback(() => {
    pollRefresh();
    window.setTimeout(() => setClientSecret(null), 2500);
  }, [pollRefresh]);

  // Shared opener: takes a resolver returning the backend session
  // ({ client_secret }); sets it to open the modal.
  const openCheckout = useCallback(
    async (resolve: () => Promise<{ client_secret: string } | null>) => {
      if (!enabled || inFlight.current) return;
      inFlight.current = true;
      setIsLoading(true);
      setError(null);
      try {
        const session = await resolve();
        if (!session || !session.client_secret) {
          setError('checkout_unavailable');
          return;
        }
        setClientSecret(session.client_secret);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[Stripe] checkout error', e);
        setError('checkout_error');
      } finally {
        inFlight.current = false;
        setIsLoading(false);
      }
    },
    [enabled],
  );

  const startCheckout = useCallback(
    (planCode: string) =>
      openCheckout(async () => {
        const result = await createCheckoutSession(planCode, accessToken);
        if (isErrorResponseResult(result)) return null;
        return result as unknown as { client_secret: string };
      }),
    [accessToken, openCheckout],
  );

  const startTopup = useCallback(
    (sku: string) =>
      openCheckout(async () => {
        const result = await createTopupSession(sku, accessToken);
        if (isErrorResponseResult(result)) return null;
        return result as unknown as { client_secret: string };
      }),
    [accessToken, openCheckout],
  );

  return {
    enabled,
    isLoading,
    error,
    clientSecret,
    startCheckout,
    startTopup,
    closeCheckout,
    handleComplete,
  };
}

export type StripeCheckout = ReturnType<typeof useStripeCheckout>;
